import React, { useState, useCallback } from 'react';
import {
  Drawer, Dialog, Box, Typography, IconButton, TextField, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCommentIcon from '@mui/icons-material/AddComment';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import { useAssistant } from '../../contexts/AssistantContext';
import { onderTestbalk } from '../../utils/testbalk';
import AssistantChatArea from './AssistantChatArea';
import AssistantAvatar from './AssistantAvatar';
import { AT } from './assistantStijl';

const DRAWER_WIDTH = 420;

export default function AssistantDrawer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    isOpen, toggle, sendMessage, isStreaming, stopStreaming,
    setConversationId, messages,
  } = useAssistant();

  const [input, setInput] = useState('');
  const [expanded, setExpanded] = useState(false);

  const handleSend = useCallback(() => {
    if (!input.trim() || isStreaming) return;
    sendMessage(input.trim());
    setInput('');
  }, [input, isStreaming, sendMessage]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleNewConversation = useCallback(() => {
    stopStreaming();
    setConversationId(null);
  }, [setConversationId, stopStreaming]);

  const handleClose = useCallback(() => {
    setExpanded(false);
    toggle();
  }, [toggle]);

  // Header bar (shared between drawer and dialog)
  const header = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        minHeight: 56,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssistantAvatar size={24} />
        <Typography variant="h6" sx={{ fontFamily: AT.font, fontSize: 16, fontWeight: 700, color: AT.text }}>
          Full Frame Assistent
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {messages.length > 0 && (
          <IconButton size="small" onClick={handleNewConversation} title="Nieuw gesprek">
            <AddCommentIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton size="small" onClick={() => setExpanded(!expanded)} title={expanded ? 'Verkleinen' : 'Vergroten'}>
          {expanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
        </IconButton>
        <IconButton size="small" onClick={handleClose} title="Sluiten">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  // Input footer (shared)
  const inputFooter = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        px: 2,
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <TextField
        fullWidth
        multiline
        maxRows={4}
        placeholder="Stel een vraag..."
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isStreaming}
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '0.75rem',
            fontSize: 14,
            bgcolor: AT.surfaceLow,
            '& fieldset': { borderColor: 'transparent' },
            '&.Mui-focused': { bgcolor: AT.surface },
            '&.Mui-focused fieldset': { borderColor: AT.primary },
          },
        }}
      />
      {isStreaming ? (
        <IconButton onClick={stopStreaming} title="Stop genereren" sx={{ mb: 0.3, color: AT.secondary }}>
          <StopIcon />
        </IconButton>
      ) : (
        <IconButton onClick={handleSend} disabled={!input.trim()} title="Verstuur" sx={{ mb: 0.3, color: AT.primary }}>
          <SendIcon />
        </IconButton>
      )}
    </Box>
  );

  // Chat content (shared)
  const chatContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {header}
      <AssistantChatArea />
      {inputFooter}
    </Box>
  );

  // Expanded mode: full-screen dialog
  if (expanded && isOpen) {
    return (
      <Dialog
        open
        fullWidth
        maxWidth="md"
        onClose={handleClose}
        PaperProps={{
          sx: {
            height: '85vh',
            maxHeight: '85vh',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {chatContent}
      </Dialog>
    );
  }

  // Compact mode: side drawer
  return (
    <Drawer
      anchor="right"
      variant={isMobile ? 'temporary' : 'persistent'}
      open={isOpen}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMobile ? '100vw' : DRAWER_WIDTH,
          maxWidth: '100vw',
          borderRadius: 0,
          boxShadow: isOpen ? '-4px 0 24px rgba(0,0,0,0.08)' : 'none',
          // Niet-modaal op desktop: onder de TEST-balk beginnen, niet erachter.
          ...(isMobile ? {} : onderTestbalk),
        },
      }}
    >
      {chatContent}
    </Drawer>
  );
}
